import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { StateComponent } from '../../../../../common/components/state/state.component';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { IFormModel } from '../../../../../common/apis/form/model/form.model';
import { IFormBettings } from '@oogaday/shared';
import { NotificationService } from '../../../../../common/services/notification.service';
import { AnalyticsService } from '../../../../../common/services/analytics.service';
import { FormBettingApiService } from '../../../../../common/apis/form/form-betting-api.service';
import { SideOverlayService } from '../../../../../common/components/side-overlay/side-overlay.service';
import { MatchStatsComponent, MatchStatsComponentData } from './match-stats/match-stats.component';
import { FormFillerComponent } from '../../../../../common/modules/form-actions/common/components/form-filler/form-filler.component';
import { ListHeaderComponent } from '../../../../../common/components/list-header/list-header.component';

@Component({
  selector: 'oo-form-betting',
  templateUrl: './form-betting.component.html',
  styleUrls: ['./form-betting.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ListHeaderComponent, FormFillerComponent],
})
export class FormBettingComponent extends StateComponent<FormBettingComponentState> implements OnInit {

  private readonly fb = inject(UntypedFormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBettingApi = inject(FormBettingApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly sideOverlayService = inject(SideOverlayService);

  state: FormBettingComponentState = {
    tournamentId: this.route.snapshot.paramMap.get('tournamentId')!,
    dataFetcher: this.dataFetcher.bind(this),
    dataSaver: this.save.bind(this),
    userBettingId: undefined,
    isFormDisabled: false,
    showStatsPanel: false,
  };

  ngOnInit(): void {
    this.analyticsService.trackScreenName('formBetting');
  }

  afterSave(): void {
    this.notificationService.notify('Betting was saved');
    this.router.navigate(['../../forms'], {
      relativeTo: this.route,
    });
  }

  toggleStatsPanel(): void {
    this.sideOverlayService.open<MatchStatsComponent, MatchStatsComponentData>(MatchStatsComponent,
      {
        data: {
          compGroupId: this.route.snapshot.paramMap.get('compGroupId')!,
        },
      });
  }

  private save(betting: IFormBettings<any>): Promise<boolean> {
    if (this.state.userBettingId) {
      return this.formBettingApi.updateBetting(
        this.route.snapshot.paramMap.get('compGroupId')!,
        this.route.snapshot.paramMap.get('formId')!,
        this.state.userBettingId,
        betting,
      ).then(() => true);
    } else {
      return this.formBettingApi.createBetting(
        this.route.snapshot.paramMap.get('compGroupId')!,
        this.route.snapshot.paramMap.get('formId')!,
        betting,
      ).then(() => true);
    }
  }

  private dataFetcher(): Promise<IFormModel> {
    return this.formBettingApi.getFormById(
      this.route.snapshot.paramMap.get('compGroupId')!,
      this.route.snapshot.paramMap.get('formId')!,
      ['userBetting', 'metadata', 'config', 'userBettingId'],
    )
      .then((form) => {
        this.updateState({
          userBettingId: form.userBettingId,
          isFormDisabled: new Date() > form.startsAt,
        });

        return form;
      });
  }
}

interface FormBettingComponentState {
  tournamentId: string;
  dataFetcher: () => Promise<IFormModel>;
  dataSaver: (betting: IFormBettings<any>) => Promise<boolean>;
  userBettingId: string | undefined;
  isFormDisabled: boolean;
  showStatsPanel: boolean;
}
